import { Badge } from '../../types/index';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { flash, people, school, sunny, calendar, checkmarkCircle, trophy, moon, book, checkmark, star, cube, person, medal, home, call, bulbOutline, close } from 'ionicons/icons';
import { IonIcon, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent } from '@ionic/react';
import { useState } from 'react';
import { cacheBustImageUrl } from '../../utils/imageUtils';

interface BadgesTableProps {
  badges: Badge[];
}

// Map badge icons to Ion icons
const getBadgeIcon = (icon: string): string => {
  const iconMap: { [key: string]: string } = {
    '⚡': flash,
    '🤝': people,
    '🎓': school,
    '🌅': sunny,
    '📅': calendar,
    '💯': checkmarkCircle,
    '🌙': moon,
    '📚': book,
    '✅': checkmark,
    '⭐': star,
    '🧩': cube,
    '👨‍🏫': person,
    '🔥': medal,
    '🏆': trophy,
    '📞': call,
    '✨': star,
    '🏠': home,
    '👋': people,
    '🧑‍🤝‍🧑': people,
    '💡': bulbOutline
  };
  return iconMap[icon] || trophy;
};

export function BadgesTable({ badges }: BadgesTableProps) {
  const isEarned = (badge: Badge) => !!badge.earnedDate;
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleBadgeClick = (badge: Badge) => {
    setSelectedBadge(badge);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBadge(null);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: 'rgba(var(--ion-color-secondary-rgb), 0.15)' }}>
          <h2 className="text-xl font-bold text-gray-800">Badges</h2>
        </div>
        <div className="px-6 py-8">
          <div className="flex flex-wrap justify-evenly gap-6">
            {badges.map((badge) => {
              const earned = isEarned(badge);
              return (
                <div
                  key={badge.id}
                  className={`flex flex-col items-center cursor-pointer transition-transform hover:scale-105 ${
                    earned ? '' : 'opacity-50 grayscale'
                  }`}
                  onClick={() => handleBadgeClick(badge)}
                >
                  <div className="mb-2">
                    {badge.imageUrl ? (
                      <img
                        src={cacheBustImageUrl(badge.imageUrl)}
                        alt={badge.name}
                        className={`w-16 h-16 object-contain pointer-events-none ${
                          earned ? '' : 'opacity-50 grayscale'
                        }`}
                      />
                    ) : badge.icon ? (
                      <IonIcon
                        icon={getBadgeIcon(badge.icon)}
                        className="w-16 h-16"
                        style={{
                          color: earned
                            ? 'var(--ion-color-primary)'
                            : 'var(--ion-color-medium)',
                        }}
                      />
                    ) : (
                      <IonIcon
                        icon={trophy}
                        className="w-16 h-16"
                        style={{
                          color: earned
                            ? 'var(--ion-color-primary)'
                            : 'var(--ion-color-medium)',
                        }}
                      />
                    )}
                  </div>
                  <div
                    className={`text-sm font-medium text-center max-w-[120px] ${
                      earned ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {badge.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <IonModal isOpen={isModalOpen} onDidDismiss={handleCloseModal}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{selectedBadge?.name || 'Badge Details'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleCloseModal}>
                <IonIcon icon={close} slot="icon-only" />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {selectedBadge && (
            <div className="py-4">
              <div className="flex flex-col items-center mb-6">
                {selectedBadge.imageUrl ? (
                  <img
                    src={cacheBustImageUrl(selectedBadge.imageUrl)}
                    alt={selectedBadge.name}
                    className={`w-20 h-20 mb-4 object-contain pointer-events-none ${
                      isEarned(selectedBadge) ? '' : 'opacity-50 grayscale'
                    }`}
                  />
                ) : selectedBadge.icon ? (
                  <IonIcon
                    icon={getBadgeIcon(selectedBadge.icon)}
                    className="w-20 h-20 mb-4"
                    style={{
                      color: isEarned(selectedBadge)
                        ? 'var(--ion-color-primary)'
                        : 'var(--ion-color-medium)',
                    }}
                  />
                ) : (
                  <IonIcon
                    icon={trophy}
                    className="w-20 h-20 mb-4"
                    style={{
                      color: isEarned(selectedBadge)
                        ? 'var(--ion-color-primary)'
                        : 'var(--ion-color-medium)',
                    }}
                  />
                )}
                <div className="text-2xl font-semibold text-gray-900 mb-2 text-center">
                  {selectedBadge.name}
                </div>
                {isEarned(selectedBadge) && selectedBadge.earnedDate && (
                  <div className="text-sm text-gray-600 mb-4">
                    Earned {formatDistanceToNow(parseISO(selectedBadge.earnedDate), { addSuffix: true })}
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4 mb-4">
                <div className="text-base font-semibold text-gray-800 mb-2">
                  Description
                </div>
                <div className="text-sm text-gray-700">
                  {selectedBadge.description}
                </div>
              </div>

              {selectedBadge.courses && selectedBadge.courses.length > 0 && (
                <div className="border-t pt-4">
                  <div className="text-base font-semibold text-gray-800 mb-3">
                    Complete these courses to earn this badge:
                  </div>
                  <ul className="list-disc list-inside space-y-2">
                    {selectedBadge.courses.map((course, index) => (
                      <li key={index} className="text-sm text-gray-700">
                        {course}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </IonContent>
      </IonModal>
    </>
  );
}
